// Feedback.tsx
import SearchBar from "pages/Admin/creditsManagement/searchbar";
import { getFeedbacks, FeedbackItem } from "apis/adminServices/Report&fFeedbackServices";
import React, { useState, useEffect } from "react";
import FeedbackFilter from "./feedbackFilter";
import Pagination from "pages/Admin/creditsManagement/pagination";
import FeedbackDetailsModal from "./feedbackDetailsModel";

const Feedback: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<FeedbackItem | null>(null);
  const [feedbacks, setFeedbacks] = useState<FeedbackItem[]>([]);
  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalItems, setTotalItems] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searchTerm, setSearchTerm] = useState<string>("");
  const [fromDate, setFromDate] = useState<string | undefined>(undefined);
  const [toDate, setToDate] = useState<string | undefined>(undefined);

  const [pageSize, setPageSize] = useState(10); // Matches API default pageSize

  useEffect(() => {
    const loadFeedbacks = async () => {
      setLoading(true);

      try {
        const isSearching = Boolean(searchTerm?.trim());

        const result = await getFeedbacks({
          pageNumber: isSearching ? 1 : currentPage, // 👈 force page 1 during search
          pageSize: pageSize,
          nameOrEmail: searchTerm?.trim()?.toLowerCase() || undefined,
          fromDate: fromDate || undefined,
          toDate: toDate || undefined,
        });

        // A search with no matches can return data as null/undefined instead of
        // an empty array — guard so feedbacks is never undefined and we don't
        // read .length on undefined.
        const items = Array.isArray(result?.data) ? result.data : [];
        setFeedbacks(items);
        // API now returns the exact total, so pagination is precise.
        setTotalItems(isSearching ? items.length : result?.totalRecords ?? 0);
      } catch (error) {
        console.error("Failed to load feedbacks:", error);
      } finally {
        setLoading(false);
      }
    };

    loadFeedbacks();
  }, [currentPage, pageSize, searchTerm, fromDate, toDate]);

  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };

  const handleViewDetails = (feedback: FeedbackItem) => {
    setSelectedFeedback(feedback);
    setIsModalOpen(true);
  };

  const handleApplyFilters = (filters: { fromDate?: string; toDate?: string }) => {
    setFromDate(filters.fromDate);
    setToDate(filters.toDate);
    setCurrentPage(1);
  };

  const handleClearFilters = () => {
    setFromDate(undefined);
    setToDate(undefined);
    setCurrentPage(1);
  };

  return (
    <section className="bg-white p-6 rounded-xl mt-6">
      <div className="mt-6">
        <div className="flex flex-wrap gap-4 items-center justify-between pb-4">
          <p className="text-grease-100 text-sm font-semibold">Feedback</p>
          <div className="flex flex-wrap items-center gap-3 relative">
            <SearchBar searchTerm={searchTerm} setSearchTerm={setSearchTerm} />
            <div className="relative">
              <button onClick={() => setIsFilterOpen(!isFilterOpen)} className="flex items-center justify-center gap-2 p-3 border bg-[#FFD700] rounded-lg">
                <i className="st-filter text-white"></i>
                <p className="font-medium text-white text-sm">Filters</p>
              </button>
              <FeedbackFilter
                isOpen={isFilterOpen}
                onClose={() => setIsFilterOpen(false)}
                onApply={handleApplyFilters}
                onClear={handleClearFilters}
              />
            </div>
          </div>
        </div>

        <div className="relative overflow-x-auto sm:rounded-lg">
          <table className="w-full text-sm text-left text-gray-500">
            <thead className="text-xs text-gray-700 p-4 bg-blue-50">
              <tr>
                <th className="px-6 py-4">Sl.No.</th>
                <th className="px-6 py-4">Customer Name</th>
                <th className="px-6 py-4">Feedback Date</th>
                <th className="px-6 py-4">Customer Email</th>
                <th className="px-6 py-4"></th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    Loading...
                  </td>
                </tr>
              ) : (feedbacks?.length ?? 0) > 0 ? (
                (feedbacks ?? []).map((feedback, index) => (
                  <tr key={feedback.id} className="odd:bg-white even:bg-blue-50 border">
                    <td className="px-6 py-4 flex items-center gap-4">{index + 1 + (currentPage - 1) * pageSize}</td>
                    <td className="px-6 py-4">{feedback.name}</td>
                    <td className="px-6 py-4">{new Date(feedback.feedbackDate).toLocaleDateString("en-GB")}</td>
                    <td className="px-6 py-4">{feedback.email}</td>
                    <td className="px-6 py-4 flex items-center justify-end gap-3">
                      <button
                        onClick={() => handleViewDetails(feedback)}
                        className="flex items-center gap-1 px-4 py-3 rounded-md border border-blue-300 text-blue-500 text-xs"
                      >
                        <span className="st-eye text-blue-500 text-xs" />
                        View Details
                      </button>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={5} className="px-6 py-4 text-center">
                    No feedback found
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>

        <Pagination
          currentPage={currentPage}
          totalItems={totalItems}
          onPageChange={handlePageChange}
          pageSize={pageSize}
          onPageSizeChange={(size) => {
            setPageSize(size);
            setCurrentPage(1);
          }}
        />

        <FeedbackDetailsModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} feedback={selectedFeedback} />
      </div>
    </section>
  );
};

export default Feedback;
